import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAgent, useUpdateAgent } from '@/lib/api/agents';
import { ApiError } from '@/lib/api/client';
import { AgentForm } from '@/routes/agents/agent-form';

export function AgentEditPage({ agentId }: { agentId: string }) {
  const agentQuery = useAgent(agentId);
  const updateAgent = useUpdateAgent(agentId);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  if (agentQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (agentQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load agent: {agentQuery.error.message}
      </p>
    );
  }

  const agent = agentQuery.data;
  const active = agent.versions.find((version) => version.id === agent.activeVersionId);
  if (!active) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-muted-foreground)]">Agent not found.</p>
        <Button asChild variant="outline">
          <Link to="/agents">Back to agents</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Edit agent</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Saving appends a new version and makes it active.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{active.name}</CardTitle>
          <CardDescription>
            Editing from v{active.versionNumber} (the active version).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AgentForm
            initial={{
              slug: active.slug,
              name: active.name,
              description: active.description,
              modelTier: active.modelTier,
              systemPrompt: active.systemPrompt,
              voicePrompt: active.voicePrompt,
              maxRequestsPerRun: active.maxRequestsPerRun,
              enabledTools: active.enabledTools,
            }}
            submitting={updateAgent.isPending}
            submitLabel="Save new version"
            error={error}
            onCancel={() => void navigate({ to: '/agents/$agentId', params: { agentId } })}
            onSubmit={(spec) => {
              setError(null);
              updateAgent.mutate(spec, {
                onSuccess: () => void navigate({ to: '/agents/$agentId', params: { agentId } }),
                onError: (caught) =>
                  setError(
                    caught instanceof ApiError ? caught.message : 'Could not save the agent.',
                  ),
              });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
